import { useState, useEffect, useRef, useMemo } from 'react';
import {
  AlertTriangle, AlertCircle, CheckCircle2,
  Loader2, RefreshCw, TrendingUp, Image as ImageIcon,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { cn, formatNumber } from '../lib/utils';
import { Card } from './ui/Card';
import Badge from './ui/Badge';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import { InlineStat } from './ui/MetricCard';
import { MARKETPLACE_META } from './marketplaces';
import { fetchActiveOrderProgress, progressCounts, createThroughputSampler, isMissingFunction, ringArcs } from '../lib/orderProgress';
import { getCached, setCached } from '../lib/cache';

// =====================================================================
// LiveDashboard — admin only — real-time metrics for active order
// Source of truth: RPC order_progress (22.10; fallback: widok dispatcher_health) + batch_jobs
// =====================================================================

// B1 (audyt bazy 20.10): 5 s gdy leci zlecenie albo grafiki A+, 15 s gdy nic się nie dzieje, pauza gdy karta w tle; bez Realtime.
// Audyt pamięci 22.10: 5 s → 15 s (baza Nano; postęp z RPC order_progress).
const POLL_INTERVAL_MS = 15000;
const IDLE_POLL_INTERVAL_MS = 15000;
const THROUGHPUT_WINDOW_MIN = 30;

const COLOR_PROCESSING = '#3B6CF6';
const COLOR_FAILED     = '#EF4444';
// Pierścień postępu (uwaga Maćka 23.10): jeden kolor — gotowe = pełny niebieski, w toku = ten sam niebieski półprzezroczysty,
// oczekujące = szary tor. Procent w środku = tylko gotowe. Kropki w kafelkach statusu muszą wyglądać tak samo jak łuki.
const RING_BLUE               = '#3B6CF6';
const RING_PROCESSING_OPACITY = 0.35;
const RING_PROCESSING_FILL    = 'rgba(59, 108, 246, 0.35)'; // = RING_BLUE przy RING_PROCESSING_OPACITY (kropka legendy)
const PENDING_GRAY            = '#94A3B8';
// PERF-UX (23.10): ostatni stan dashboardu w cache — drugie wejście renderuje się od razu, odświeżenie w tle.
const SNAPSHOT_KEY = 'live:snapshot';
// Próbki przepustowości na poziomie modułu — wykres nie zaczyna od zera po powrocie na ekran.
const SAMPLER = createThroughputSampler(THROUGHPUT_WINDOW_MIN);

const BATCH_STATUS_VARIANT = {
  pending:    'neutral',
  processing: 'brand',
  done:       'success',
  failed:     'danger',
};

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDuration(seconds) {
  if (!seconds || isNaN(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function formatETA(remaining, throughputPerMin) {
  if (!throughputPerMin || throughputPerMin <= 0 || !remaining) return '—';
  const minutes = remaining / throughputPerMin;
  if (minutes < 60) return `~${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `~${h}h ${m}m`;
}

// ─── Throughput chart (SVG, no deps) ──────────────────────────────
function ThroughputChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-44 flex items-center justify-center text-sm text-ink-400 dark:text-ink-500">
        Brak danych throughput
      </div>
    );
  }
  const W = 800, H = 200, P = 28;
  const max = Math.max(...data.map(d => d.value), 1);
  const xStep = (W - P * 2) / Math.max(data.length - 1, 1);
  const points = data.map((d, i) => {
    const x = P + i * xStep;
    const y = H - P - (d.value / max) * (H - P * 2);
    return `${x},${y}`;
  }).join(' ');
  const areaPath = `M ${P},${H - P} L ${points.split(' ')[0]} L ${points.replace(/ /g, ' L ')} L ${P + (data.length - 1) * xStep},${H - P} Z`;
  const last = data[data.length - 1]?.value ?? 0;
  const avg = Math.round(data.reduce((s, d) => s + d.value, 0) / data.length);
  const lastX = P + (data.length - 1) * xStep;
  const lastY = H - P - (last / max) * (H - P * 2);

  return (
    <div className="relative">
      <div className="absolute top-0 right-0 flex gap-4 text-xs">
        <span className="text-ink-500 dark:text-ink-400">
          Aktualnie:{' '}
          <strong className="text-brand-700 dark:text-brand-300 font-bold tabular-nums">{last}</strong>
          <span className="text-ink-400 dark:text-ink-500 ml-0.5">/min</span>
        </span>
        <span className="text-ink-500 dark:text-ink-400">
          Średnio: <strong className="text-ink-800 dark:text-ink-200 font-bold tabular-nums">{avg}</strong>
          <span className="text-ink-400 dark:text-ink-500 ml-0.5">/min</span>
        </span>
        <span className="text-ink-500 dark:text-ink-400">
          Max: <strong className="text-ink-800 dark:text-ink-200 font-bold tabular-nums">{max}</strong>
          <span className="text-ink-400 dark:text-ink-500 ml-0.5">/min</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-44 mt-3">
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={COLOR_PROCESSING} stopOpacity="0.32" />
            <stop offset="100%" stopColor={COLOR_PROCESSING} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Grid */}
        {[0.25, 0.5, 0.75].map(p => (
          <line key={p}
            x1={P} y1={H - P - p * (H - P * 2)}
            x2={W - P} y2={H - P - p * (H - P * 2)}
            stroke="currentColor" strokeDasharray="3 5" className="text-ink-200 dark:text-ink-800" />
        ))}
        {/* Area */}
        <path d={areaPath} fill="url(#areaGrad)" />
        {/* Line */}
        <polyline points={points} fill="none" stroke={COLOR_PROCESSING} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {/* Last point — pulsing */}
        <g>
          <circle cx={lastX} cy={lastY} r="14" fill={COLOR_PROCESSING} opacity="0.18">
            <animate attributeName="r" values="6;16;6" dur="1.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.4;0;0.4" dur="1.6s" repeatCount="indefinite" />
          </circle>
          <circle cx={lastX} cy={lastY} r="5" fill="#fff" stroke={COLOR_PROCESSING} strokeWidth="2.5" />
        </g>
      </svg>
    </div>
  );
}

// ─── Radial progress gauge (centerpiece visual) ───────────────────
//
// Multi-segment ring (Done / Processing / Failed). Tylko OSTATNI widoczny segment
// dostaje strokeLinecap="round" (clean cap), pozostałe "butt" (żeby się nie zachodziły).
// Done = pełny niebieski, Processing = ten sam niebieski z przezroczystością (zaraz za Done), reszta = szary tor (Pending).
//
function RadialProgress({ done, processing, pending, failed, total, productsDone, productsTotal, unit = 'produktów', countsUnit = 'product' }) {
  const size = 280;
  const stroke = 22;
  const radius = (size - stroke) / 2;
  const C = 2 * Math.PI * radius;
  // Poprawka v13 (24.10, C): łuki z czystej funkcji — „w toku” tylko z liczników PER PRODUKT (engine_products.running);
  // z liczników po batchach (start zlecenia, zanim powstaną engine_products) nie ma łuku „w toku” — reszta = szary tor.
  const { doneArc, processingArc, failedArc } = ringArcs({ C, total, done, processing, failed, productsDone, productsTotal, countsUnit });
  const safeProductsTotal = Math.max(productsTotal, 1);
  const pctDone = (productsDone / safeProductsTotal) * 100;

  // Który segment jest "ostatni widoczny" — dostaje round cap, reszta butt
  const lastSegment =
    failedArc > 0 ? 'failed' :
    processingArc > 0 ? 'processing' :
    'done';

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90 transform">
        {/* Track */}
        <circle
          cx={size/2} cy={size/2} r={radius}
          fill="none" stroke="currentColor" strokeWidth={stroke}
          className="text-ink-100 dark:text-ink-800"
        />
        {/* Done arc — pełny niebieski (sterowane productsDone, nie liczbą done batchy) */}
        {doneArc > 0 && (
          <circle
            cx={size/2} cy={size/2} r={radius}
            fill="none" stroke={RING_BLUE} strokeWidth={stroke}
            strokeDasharray={`${doneArc} ${C}`}
            strokeLinecap={lastSegment === 'done' ? 'round' : 'butt'}
            className="transition-[stroke-dasharray] duration-700 ease-out"
          />
        )}
        {/* Processing arc — ten sam niebieski, półprzezroczysty, zaraz za Done */}
        {processingArc > 0 && (
          <circle
            cx={size/2} cy={size/2} r={radius}
            fill="none" stroke={RING_BLUE} strokeOpacity={RING_PROCESSING_OPACITY} strokeWidth={stroke}
            strokeDasharray={`${processingArc} ${C}`}
            strokeDashoffset={-doneArc}
            strokeLinecap={lastSegment === 'processing' ? 'round' : 'butt'}
            className="transition-all duration-700 ease-out"
          />
        )}
        {/* Failed arc — red */}
        {failedArc > 0 && (
          <circle
            cx={size/2} cy={size/2} r={radius}
            fill="none" stroke={COLOR_FAILED} strokeWidth={stroke}
            strokeDasharray={`${failedArc} ${C}`}
            strokeDashoffset={-(doneArc + processingArc)}
            strokeLinecap="round"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center flex-col">
        <div className="text-6xl font-bold tabular-nums tracking-tighter leading-none bg-gradient-to-br from-brand-700 to-brand-900 bg-clip-text text-transparent dark:from-brand-200 dark:to-brand-400">
          {pctDone.toFixed(1)}<span className="text-3xl text-ink-400 dark:text-ink-500 font-normal">%</span>
        </div>
        <div className="text-sm text-ink-500 dark:text-ink-400 mt-2 font-medium tabular-nums">
          {formatNumber(productsDone)} / {formatNumber(productsTotal)} {unit}
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-success-light/50 dark:bg-success/10">
          <span className="live-dot" />
          <span className="text-2xs uppercase font-bold tracking-wide text-success-dark dark:text-success">In progress</span>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ count, label, color, isActive }) {
  return (
    <div className={cn(
      'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
      isActive ? 'bg-white dark:bg-ink-900 border border-ink-200 dark:border-ink-800' : 'bg-ink-50/60 dark:bg-ink-800/30',
    )}>
      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
      <div className="flex-1 min-w-0">
        <div className="text-2xs uppercase font-bold tracking-wide text-ink-500 dark:text-ink-400">{label}</div>
        <div className="text-xl font-bold tabular-nums tracking-tighter text-ink-900 dark:text-ink-50 leading-none mt-0.5">
          {formatNumber(count)}
        </div>
      </div>
    </div>
  );
}

// ─── A+ throughput helper (grafiki/min, to samo okno co produkty) ──────
function buildThroughput(rows) {
  const buckets = new Map();
  for (let i = THROUGHPUT_WINDOW_MIN - 1; i >= 0; i--) {
    const minute = new Date(Date.now() - i * 60 * 1000);
    minute.setSeconds(0, 0);
    buckets.set(minute.toISOString(), 0);
  }
  for (const r of rows) {
    const m = new Date(r.created_at);
    m.setSeconds(0, 0);
    const key = m.toISOString();
    if (buckets.has(key)) buckets.set(key, buckets.get(key) + 1);
  }
  return Array.from(buckets.entries()).map(([t, v]) => ({ time: t, value: v }));
}

// ─── A+ job card — ten sam wygląd co zwykłe zlecenie, oznaczony A+ ─────
function AplusJobCard({ job }) {
  const meta = MARKETPLACE_META[job.marketplace] || {};
  const remaining = Math.max(0, job.expected - job.panelsDone);
  const recentPerMin = job.throughputData.length > 0
    ? Math.round(job.throughputData.slice(-5).reduce((s, d) => s + d.value, 0) / 5)
    : 0;
  const eta = formatETA(remaining, recentPerMin);

  return (
    <div className="space-y-6 rounded-xl border-2 border-violet-200 dark:border-violet-900/50 bg-violet-50/30 dark:bg-violet-950/10 p-4 lg:p-5">
      <Card className="p-5 lg:p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center font-bold text-lg flex-shrink-0 relative"
              style={{ background: meta.color || '#7C3AED', color: '#fff' }}>
              {meta.initial || '?'}
              <span className="absolute -bottom-1.5 -right-1.5 px-1 py-0.5 rounded bg-violet-600 text-white text-[9px] font-bold leading-none border border-white dark:border-ink-900">A+</span>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h2 className="text-lg lg:text-xl font-bold tracking-tighter text-ink-900 dark:text-ink-50">
                  {job.marketplace}
                  <span className="text-ink-300 dark:text-ink-600 mx-1.5 font-normal">/</span>
                  {job.language}
                </h2>
                <Badge variant="brand" size="md" dot>A+ Content</Badge>
              </div>
              <div className="text-sm text-ink-500 dark:text-ink-400">
                Generowanie grafik A+ · {formatNumber(job.panelsDone)}/{formatNumber(job.expected)} paneli ·{' '}
                <span className="font-mono text-ink-600 dark:text-ink-300">{job.order_id?.slice(0, 8)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <HeroStat label="ETA" value={eta} />
            <span className="w-px h-9 bg-ink-200 dark:bg-ink-800" />
            <HeroStat label="Tempo" value={<>{formatNumber(recentPerMin)}<span className="text-sm text-ink-400 dark:text-ink-500 ml-0.5 font-medium">/min</span></>} />
          </div>
        </div>
      </Card>

      <Card className="p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-center">
          <div className="flex justify-center">
            <RadialProgress
              done={job.panelsDone} processing={0} pending={remaining} failed={0}
              total={job.expected} productsDone={job.panelsDone} productsTotal={job.expected}
              unit="paneli"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <StatusPill count={job.panelsDone} label="Gotowe panele" color={RING_BLUE} isActive />
            <StatusPill count={remaining} label="W toku" color={RING_PROCESSING_FILL} isActive={remaining > 0} />
            <StatusPill count={Math.min(job.product_count || 0, 5)} label="Produkty" color={PENDING_GRAY} isActive />
            <StatusPill count={3} label="Panele/produkt" color={PENDING_GRAY} isActive />
          </div>
        </div>
      </Card>

      <Card className="p-5 lg:p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-bold text-ink-900 dark:text-ink-50 tracking-tightish flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            Grafiki A+ · ostatnie {THROUGHPUT_WINDOW_MIN} min
          </h3>
          <span className="text-2xs text-ink-400 dark:text-ink-500 uppercase tracking-wide font-bold">auto · 5s</span>
        </div>
        <ThroughputChart data={job.throughputData} />
      </Card>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────
// A+ CONTENT: osobny tor (orders.aplus_status), nie tworzy batch_jobs → dispatcher_health go nie widzi.
// Dociągamy ręcznie i pokazujemy tak jak zlecenie. PERF-UX: jedno zapytanie o wyniki A+ dla wszystkich zleceń w toku zamiast N.
async function fetchAplusJobs() {
  const { data: aplusOrders } = await supabase
    .from('orders')
    .select('id, marketplace, language, product_count, aplus_status, aplus_expected, aplus_started_at')
    .eq('aplus_status', 'processing')
    .order('aplus_started_at', { ascending: false, nullsFirst: false })
    .limit(6);
  const aplusIds = (aplusOrders ?? []).map(ao => ao.id);
  const { data: aplusRows } = aplusIds.length
    ? await supabase.from('aplus_results').select('order_id, created_at').in('order_id', aplusIds)
    : { data: [] };
  return (aplusOrders ?? []).map(ao => {
    const rows = (aplusRows ?? []).filter(r => r.order_id === ao.id);
    return {
      order_id: ao.id,
      marketplace: ao.marketplace,
      language: ao.language,
      product_count: ao.product_count,
      expected: ao.aplus_expected || (Math.min(ao.product_count || 0, 5) * 3) || 1,
      panelsDone: rows.length,
      started_at: ao.aplus_started_at,
      throughputData: buildThroughput(rows),
    };
  });
}

let snapshotRpcMissing = false;
async function fetchSnapshot() {
  if (!snapshotRpcMissing) {
    const { data, error } = await supabase.rpc('live_dashboard_snapshot');
    if (!error && data) {
      const jobs = (data.aplus || []).map(ao => {
        const rows = (ao.panels || []).map(created_at => ({ created_at }));
        return {
          order_id: ao.order_id,
          marketplace: ao.marketplace,
          language: ao.language,
          product_count: ao.product_count,
          expected: ao.aplus_expected || (Math.min(ao.product_count || 0, 5) * 3) || 1,
          panelsDone: rows.length,
          started_at: ao.aplus_started_at,
          throughputData: buildThroughput(rows),
        };
      });
      return [{ data: data.order || null }, jobs];
    }
    // Audyt 24.10: jedno kryterium „brak funkcji” (jak order_progress) — inny błąd RPC nie wyłącza migawki na stałe.
    if (error) {
      if (isMissingFunction(error)) snapshotRpcMissing = true;
      console.warn('[live_snapshot] fallback na osobne zapytania:', error.message || error);
    }
  }
  return Promise.all([fetchActiveOrderProgress(), fetchAplusJobs()]);
}

export default function LiveDashboard() {
  // PERF-UX (23.10): start z ostatniej migawki (jeśli jest) — bez „Wczytywanie postępu…”; świeże dane dociągają się w tle.
  const [snap] = useState(() => getCached(SNAPSHOT_KEY));
  const [activeOrder, setActiveOrder] = useState(snap?.data?.activeOrder ?? null);
  const [throughputData, setThroughputData] = useState(() => (snap?.data?.activeOrder?.order_id ? SAMPLER.buckets() : []));
  const [recentBatches, setRecentBatches] = useState(snap?.data?.recentBatches ?? []);
  const [loading, setLoading] = useState(!snap);
  const [lastUpdate, setLastUpdate] = useState(snap?.at ? new Date(snap.at) : null);
  const [aplusJobs, setAplusJobs] = useState(snap?.data?.aplusJobs ?? []);
  const [throughputPerMin, setThroughputPerMin] = useState(() => (snap?.data?.activeOrder?.order_id ? SAMPLER.perMin(5) : null));
  const sampler = useRef(SAMPLER);

  const fetchData = async () => {
    try {
      // Preferuj order który FAKTYCZNIE leci (processing) nad queued.
      // 'processing' < 'queued' alphabetically → ASC daje processing first.
      // Wśród równych → FIFO po created_at ASC.
      // 22.10: RPC order_progress (per produkt z engine_products), fallback na widok dispatcher_health.
      // PERF-UX (23.10): oba tory (aktywne zlecenie → postęp, A+ → wyniki A+) idą równolegle, każdy ze swoim drugim krokiem;
      // ekran rysuje się po nich, a lista batchy (tylko do karty „Failed”) dochodzi po narysowaniu.
      // PERF (23.10 wieczór): jedno żądanie RPC live_dashboard_snapshot (t75) zamiast 2 × 2 kolejnych — każde żądanie REST
      // na bazie Nano to 0,6–1 s w ciszy i 4–9 s w czasie zleceń (edge_logs 23.10). Brak funkcji → stare zapytania.
      const [{ data: health }, jobs] = await fetchSnapshot();

      const order = health ?? null;
      setActiveOrder(order);
      setAplusJobs(jobs);

      if (order?.order_id) {
        // Przepustowość z przyrostu results_saved (= produkty zakończone w engine_products) między odczytami —
        // liczenie z batchy (batch na dev = 400 produktów) nic nie mówiło, a batch_results engine nie zapisuje na bieżąco.
        sampler.current.push(order.order_id, order.results_saved || 0);
        setThroughputData(sampler.current.buckets());
        setThroughputPerMin(sampler.current.perMin(5));
      } else {
        sampler.current.reset();
        setThroughputData([]);
        setThroughputPerMin(null);
      }
      setLoading(false);

      let batches = [];
      if (order?.order_id) {
        const { data } = await supabase
          .from('batch_jobs')
          .select('id, batch_index, status, n8n_execution_id, retry_count, dispatched_at, finished_at, error_message')
          .eq('order_id', order.order_id)
          .in('status', ['processing', 'failed'])
          .order('dispatched_at', { ascending: false, nullsFirst: false })
          .limit(20);
        batches = data ?? [];
      }
      setRecentBatches(batches);
      setCached(SNAPSHOT_KEY, { activeOrder: order, aplusJobs: jobs, recentBatches: batches });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
      setLastUpdate(new Date());
    }
  };

  const activeRef = useRef(false);
  activeRef.current = !!activeOrder?.order_id || aplusJobs.length > 0;
  useEffect(() => {
    fetchData();
    let timer = null;
    const schedule = () => {
      timer = setTimeout(async () => {
        if (document.visibilityState !== 'hidden') await fetchData();
        schedule();
      }, activeRef.current ? POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS);
    };
    schedule();
    const onVisible = () => { if (document.visibilityState === 'visible') fetchData(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearTimeout(timer); document.removeEventListener('visibilitychange', onVisible); };
    // eslint-disable-next-line
  }, []);

  const stats = useMemo(() => {
    if (!activeOrder) return null;
    // 22.10: z RPC liczniki PER PRODUKT (engine_products), z widoku — po batchach (lib/orderProgress.progressCounts).
    const c = progressCounts(activeOrder);
    const { total, done, pending, processing, failed, unit } = c;
    const productsTotal = c.productsTotal || (total * 5);
    const productsDone = c.productsDone;
    const progress = (productsDone / Math.max(productsTotal, 1)) * 100;
    const remaining = productsTotal - productsDone;

    const recentResultsPerMin = throughputPerMin != null && throughputPerMin > 0 ? Math.round(throughputPerMin * 10) / 10 : 0;
    const eta = formatETA(remaining, recentResultsPerMin);

    return {
      unit, total, done, pending, processing, failed,
      progress, productsTotal, productsDone, remaining,
      throughputPerMin: recentResultsPerMin,
      avgBatchMin: activeOrder.avg_batch_min,
      stuckOver30: activeOrder.stuck_over_30min || 0,
      stuckOver60: activeOrder.stuck_over_60min || 0,
      retried: activeOrder.retried_batches || 0,
      eta,
    };
  }, [activeOrder, throughputPerMin]);

  // Loading state
  if (loading && !activeOrder && aplusJobs.length === 0) {
    return (
      <div className="flex items-center justify-center py-20 gap-3 text-ink-500 dark:text-ink-400">
        <Loader2 className="w-5 h-5 animate-spin text-brand-600" />
        <span className="text-sm font-medium">Wczytywanie postępu…</span>
      </div>
    );
  }

  // No active order — empty state
  if (!activeOrder && aplusJobs.length === 0) {
    return (
      <div className="animate-fade-in">
        <PageHeader
          title="Live Dashboard"
          badge={<Badge variant="success" size="md" dot>System idle</Badge>}
          subtitle={`Monitoring kolejki. Odświeżanie co ${IDLE_POLL_INTERVAL_MS / 1000} s.`}
        />
        <EmptyState
          tone="success"
          size="lg"
          icon={<CheckCircle2 className="w-8 h-8" />}
          title="Brak aktywnych zleceń"
          description="Wszystkie zlecenia zakończone lub czekają w kolejce. Dashboard ożyje gdy dispatcher chwyci nowy batch."
        />
      </div>
    );
  }

  const marketplaceMeta = activeOrder ? (MARKETPLACE_META[activeOrder.marketplace] || {}) : {};
  const hasAlerts = !!activeOrder && (stats?.stuckOver60 > 0 || stats?.failed > 0 || stats?.stuckOver30 > 5);

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title="Live Dashboard"
        badge={<Badge variant="brand" size="md" dot>Realtime</Badge>}
        subtitle={`Auto-refresh co ${POLL_INTERVAL_MS / 1000}s · ostatnia aktualizacja ${formatTime(lastUpdate?.toISOString())}`}
        action={
          <button
            onClick={() => fetchData()}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded text-xs font-semibold text-ink-600 hover:text-ink-900 hover:bg-ink-100 dark:text-ink-400 dark:hover:text-ink-100 dark:hover:bg-ink-800 transition-colors"
            title="Wymuś odświeżenie"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Odśwież
          </button>
        }
      />

      {activeOrder && (
      <>
      {/* Order hero card — clean white panel (Linear/Stripe vibe) */}
      <Card className="p-5 lg:p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-3.5 min-w-0">
            {/* Marketplace logo */}
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center font-bold text-lg flex-shrink-0"
              style={{ background: marketplaceMeta.color || '#3B6CF6', color: '#fff' }}
            >
              {marketplaceMeta.initial || '?'}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h2 className="text-lg lg:text-xl font-bold tracking-tighter text-ink-900 dark:text-ink-50">
                  {activeOrder.marketplace}
                  <span className="text-ink-300 dark:text-ink-600 mx-1.5 font-normal">/</span>
                  {activeOrder.language}
                </h2>
                <Badge variant="success" size="md" dot>Live</Badge>
              </div>
              <div className="text-sm text-ink-500 dark:text-ink-400">
                {formatNumber(activeOrder.product_count)} produktów ·{' '}
                <span className="font-mono text-ink-600 dark:text-ink-300">{activeOrder.order_id?.slice(0, 8)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <HeroStat label="ETA" value={stats?.eta} />
            <span className="w-px h-9 bg-ink-200 dark:bg-ink-800" />
            <HeroStat
              label="Throughput"
              value={
                <>
                  {formatNumber(stats?.throughputPerMin)}
                  <span className="text-sm text-ink-400 dark:text-ink-500 ml-0.5 font-medium">/min</span>
                </>
              }
            />
          </div>
        </div>

      </Card>

      {/* Alerts */}
      {hasAlerts && (
        <div className="space-y-2">
          {stats.failed > 0 && (
            <AlertBox tone="danger" icon={AlertCircle}>
              <strong>{formatNumber(stats.failed)} {stats.unit === 'product' ? 'produktów z błędem' : 'batches FAILED'}</strong> — przekroczyły max retry. Wymagają manualnej interwencji.
            </AlertBox>
          )}
          {stats.stuckOver60 > 0 && (
            <AlertBox tone="warning" icon={AlertTriangle}>
              <strong>{stats.stuckOver60} batches stuck &gt;60 min</strong> — recovery powinna je przejąć przy następnym callback.
            </AlertBox>
          )}
          {stats.stuckOver30 > 5 && (
            <AlertBox tone="warning" icon={AlertTriangle}>
              {stats.stuckOver30} batches stuck &gt;30 min — vLLM saturation możliwe.
            </AlertBox>
          )}
        </div>
      )}

      {/* Visual progress block — radial gauge + status breakdown */}
      <Card className="p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-center">
          {/* Radial gauge */}
          <div className="flex justify-center">
            <RadialProgress
              countsUnit={stats?.unit || 'batch'}
              done={stats?.done || 0}
              processing={stats?.processing || 0}
              pending={stats?.pending || 0}
              failed={stats?.failed || 0}
              total={stats?.total || 0}
              productsDone={stats?.productsDone || 0}
              productsTotal={stats?.productsTotal || 0}
            />
          </div>

          {/* Status breakdown */}
          <div className="grid grid-cols-2 gap-3">
            <StatusPill
              count={stats?.done || 0}
              label="Done"
              color={RING_BLUE}
              isActive
            />
            <StatusPill
              count={stats?.processing || 0}
              label="Processing"
              color={RING_PROCESSING_FILL}
              isActive={(stats?.processing || 0) > 0}
            />
            <StatusPill
              count={stats?.pending || 0}
              label="Pending"
              color={PENDING_GRAY}
              isActive={(stats?.pending || 0) > 0}
            />
            <StatusPill
              count={stats?.failed || 0}
              label="Failed"
              color={stats?.failed > 0 ? COLOR_FAILED : PENDING_GRAY}
              isActive={(stats?.failed || 0) > 0}
            />
          </div>
        </div>
      </Card>

      {/* Throughput chart */}
      <Card className="p-5 lg:p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-bold text-ink-900 dark:text-ink-50 tracking-tightish flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-brand-600 dark:text-brand-400" />
            Throughput · ostatnie {THROUGHPUT_WINDOW_MIN} min
          </h3>
          <span className="text-2xs text-ink-400 dark:text-ink-500 uppercase tracking-wide font-bold">
            auto · {POLL_INTERVAL_MS / 1000}s
          </span>
        </div>
        <ThroughputChart data={throughputData} />
      </Card>

      {/* Recent batches — pokazuje się TYLKO gdy są failed (mniej szumu w idle) */}
      {recentBatches.some(b => b.status === 'failed') && (
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100 dark:border-ink-800">
          <h3 className="text-sm font-bold text-ink-900 dark:text-ink-50 tracking-tightish flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-danger" />
            Failed Batches — wymagają interwencji
          </h3>
          <Badge variant="danger" size="md">{recentBatches.filter(b => b.status === 'failed').length}</Badge>
        </div>
        <div className="divide-y divide-ink-100 dark:divide-ink-800">
          {recentBatches.filter(b => b.status === 'failed').map(b => {
            const dispatched = b.dispatched_at ? new Date(b.dispatched_at) : null;
            const ended = b.finished_at ? new Date(b.finished_at) : new Date();
            const durationSec = dispatched ? (ended - dispatched) / 1000 : null;
            return (
              <div key={b.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-ink-50/60 dark:hover:bg-ink-800/30 transition-colors">
                <div className="w-8 h-8 rounded-md bg-danger-light/60 dark:bg-danger/15 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-4 h-4 text-danger-dark" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-sm text-ink-900 dark:text-ink-50">#{b.batch_index}</span>
                    {(b.retry_count ?? 0) > 0 && (
                      <Badge variant="warning" size="sm">{b.retry_count}/3 retries</Badge>
                    )}
                    <span className="text-2xs text-ink-500 dark:text-ink-400 font-mono">
                      {formatTime(b.dispatched_at)} · {formatDuration(durationSec)}
                    </span>
                  </div>
                  {b.error_message && (
                    <div className="text-xs text-ink-500 dark:text-ink-400 mt-0.5 truncate">
                      {b.error_message}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      )}
      </>
      )}

      {aplusJobs.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center gap-2.5">
            <ImageIcon className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            <h2 className="text-sm font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">
              A+ Content — generowanie grafik
            </h2>
            <Badge variant="brand" size="md" dot>{aplusJobs.length} w toku</Badge>
          </div>
          {aplusJobs.map(job => <AplusJobCard key={job.order_id} job={job} />)}
        </div>
      )}
    </div>
  );
}

// HeroStat zastąpiony przez InlineStat z ui/MetricCard.js
const HeroStat = InlineStat;

function AlertBox({ tone, icon: Icon, children }) {
  const cls = {
    danger:  'bg-danger-light/60  border-danger/20  text-danger-dark',
    warning: 'bg-warning-light/60 border-warning/20 text-warning-dark',
    info:    'bg-info-light/60    border-info/20    text-info-dark',
  }[tone];
  return (
    <div className={cn('flex items-start gap-2.5 p-3.5 rounded-lg border text-sm font-medium', cls)}>
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div className="flex-1">{children}</div>
    </div>
  );
}
